import React, { FunctionComponent, KeyboardEvent, useCallback, useMemo, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { Keyboard, Platform, View, ViewStyle } from 'react-native'

import { Image as ExpoImage } from 'expo-image'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { KEYS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { CheckboxButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { BaseInput, BaseInputProps } from './../formElements/BaseInput'
import { FieldBadge } from './FieldBadge'
import {
  CHECKBOX_HEIGHT_AND_WIDTH_IN_PX,
  CHECKBOX_TEXT_OUTLINE_MARGIN_RIGHT_IN_PX,
  CHECKBOX_TEXT_OUTLINE_MARGIN_TOP_IN_PX,
  getCheckboxButtonStyles,
} from './styles/checkboxStyles'
import { BadgeVariant } from '../../badge/constants'
import { AccessibilityRolesNative, breakpoints, IS_WEB } from '../../constants'
import { useAccessibilityFocus } from '../../hooks/useAccessibilityFocus'
import { useSetAriaDescribedBy } from '../../hooks/useSetAriaDescribedBy'
import { getFocusBoxShadow } from '../../styles/commonStyles'
import { BodyTextSize, getFontStyles } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BodyText } from '../bodyText/BodyText'
import { CheckMarkIcon } from '../icons/CheckMarkIcon'
import { Link } from '../link/Link'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'
import { StyledMarkdown } from '../styledMarkdown/StyledMarkdown'
import { ToolTipTriggerConfig } from '../tooltip/Tooltip'

export const checkBoxButtonTypeHasOutline = {
  [CheckboxButtonType.TEXT_OUTLINE]: false,
  [CheckboxButtonType.CHECKBOX_TEXT_OUTLINE]: true,
  [CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE]: false,
  [CheckboxButtonType.CHECKBOX_DESCRIPTION_OUTLINE]: true,
  [CheckboxButtonType.GRID]: false,
}

export enum GridCheckboxSize {
  DEFAULT = 'default',
  SMALL = 'small',
}

export const getCheckboxButtonWidth = (parentContainerWidth: number, gridButtonsGap: number) => {
  const itemsPerRow = parentContainerWidth > breakpoints.mobileS ? 3 : parentContainerWidth > 300 ? 2 : 1
  const totalGapSize = itemsPerRow * gridButtonsGap
  const buttonWidth = (parentContainerWidth - totalGapSize) / itemsPerRow
  return buttonWidth
}

const CheckboxImageContainer = styled(View)<{ buttonType: CheckboxButtonType; size?: GridCheckboxSize }>(({
  theme: { spacing },
  buttonType,
  size,
}) => {
  const imageSize = size === GridCheckboxSize.SMALL ? '40px' : '48px'
  return {
    position: 'relative',
    height: imageSize,
    width: imageSize,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    marginLeft: spacing['8px'],
    marginRight: spacing['8px'],
    ...(buttonType === CheckboxButtonType.GRID && { marginBottom: '8px' }),
  }
})

const OutlineText = styled(BodyText)({
  alignSelf: 'center',
  flex: 1,
})

const GridText = styled(BodyText)({
  flexShrink: 0,
  alignSelf: 'center',
  textAlign: 'center',
})

const GridContent = styled(View)({
  flex: 1,
  flexShrink: 0,
  flexDirection: 'column',
  alignItems: 'center',
  margin: 'auto',
})

const RadioButtonImageTransition = styled(View)<{ scaled?: boolean }>(({ scaled }) => ({
  ...(IS_WEB && { transition: 'transform 0.3s' }),
  position: 'absolute',
  height: '100%',
  width: '100%',
  transform: scaled ? `scale(${53 / 48})` : 'scale(1)',
}))

const CheckboxButtonContainer = styled(PressableOpacity)<{
  theme: ThemeType
  buttonType: CheckboxButtonType
  selected?: boolean
  hovered?: boolean
  parentContainerWidth?: number
  gridButtonsGap: number
  gridCheckboxSize?: GridCheckboxSize
}>(({ theme, buttonType, selected, hovered, parentContainerWidth, gridButtonsGap, gridCheckboxSize }) => ({
  width: '100%',
  flexDirection: 'row',
  alignItems: 'flex-start',
  ...(IS_WEB && {
    outlineWidth: '0px',
  }),
  ...(buttonType === CheckboxButtonType.CHECKBOX_TEXT_OUTLINE && {
    borderRadius: '32px',
    padding: '16px 24px',
    border: `1px solid ${
      selected || hovered ? theme.colors.checkboxOutlineSelected : theme.colors.checkboxOutlineInactive
    }`,
    /* primary_teal1 is not available in colorTokens */
    backgroundColor: selected ? theme.colors.backgroundHighlightPrimary : theme.colors.checkboxBackgroundPrimary,
  }),
  ...(buttonType === CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE && {
    borderRadius: '8px',
    backgroundColor: theme.colors.checkboxBackgroundPrimary,
  }),
  ...(buttonType === CheckboxButtonType.CHECKBOX_DESCRIPTION_OUTLINE && {
    alignItems: 'center',
    borderRadius: '32px',
    padding: '16px 24px',
    border: `1px solid ${
      selected || hovered ? theme.colors.checkboxOutlineSelected : theme.colors.checkboxOutlineInactive
    }`,
    backgroundColor: selected ? theme.colors.backgroundHighlightPrimary : theme.colors.checkboxBackgroundPrimary,
  }),
  ...(buttonType === CheckboxButtonType.GRID &&
    parentContainerWidth && {
      height: gridCheckboxSize === GridCheckboxSize.SMALL ? 128 : 136,
      padding: gridCheckboxSize === GridCheckboxSize.SMALL ? 12 : 16,
      borderRadius: gridCheckboxSize === GridCheckboxSize.SMALL ? 16 : 24,
      border: `1px solid ${
        selected
          ? theme.colors.checkboxOutlineSelected
          : hovered
            ? theme.colors.checkboxOutlineHovered
            : theme.colors.checkboxOutlineInactive
      }`,
      backgroundColor: selected
        ? theme.colors.checkboxBackgroundPrimarySelected
        : theme.colors.checkboxBackgroundPrimary,
      width: `${getCheckboxButtonWidth(Math.floor(parentContainerWidth), gridButtonsGap)}px`,
      margin: `${gridButtonsGap / 2}px`,
    }),
}))

const CheckboxTextContainer = styled.View({
  flexShrink: '1',
  flexGrow: '1',
})

const CheckboxOutlinedTextContainer = styled(CheckboxTextContainer)({
  flexDirection: 'row',
})

const BadgeContainer = styled.View({
  alignSelf: 'center',
})

const TextContainer = styled.View({
  flexShrink: 1,
})

const IconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['8px'],
}))

const SubTextFieldContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['8px'],
  marginLeft: theme.spacing['44px'],
}))

const TEXT_CONTAINER_STYLE_FOCUSABLE_TITLE = {
  marginTop: -(CHECKBOX_HEIGHT_AND_WIDTH_IN_PX + CHECKBOX_TEXT_OUTLINE_MARGIN_TOP_IN_PX),
  left: CHECKBOX_HEIGHT_AND_WIDTH_IN_PX + CHECKBOX_TEXT_OUTLINE_MARGIN_RIGHT_IN_PX,
  width: `calc(100% - ${CHECKBOX_HEIGHT_AND_WIDTH_IN_PX + CHECKBOX_TEXT_OUTLINE_MARGIN_RIGHT_IN_PX}px)`,
}

const WithBaseInput = (props: BaseInputProps & { hideBaseInput?: boolean; children: React.ReactNode }) =>
  props.hideBaseInput ? <>{props.children}</> : <BaseInput {...props}>{props.children}</BaseInput>

export const Checkbox: FunctionComponent<CheckBoxProps> = ({
  testID,
  label,
  title,
  titleColor,
  description,
  checked,
  onPress,
  error,
  name,
  readOnly,
  children,
  labelSize = BodyTextSize.DEFAULT,
  badge,
  buttonType = CheckboxButtonType.TEXT_OUTLINE,
  onFocus = noop,
  onBlur = noop,
  baseInputStyle,
  markdownStyle,
  active,
  accessibilityLabelledBy,
  accessibilityLabel,
  checkboxAccessibilityLabel,
  hideErrorMessage,
  styleDescriptionAsTitle,
  descriptionSize,
  descriptionColor,
  textContainerStyle,
  customErrorMessageId,
  icon,
  checkboxStyle,
  inputContainerStyle = {},
  isTitleFocusable = false,
  parentContainerWidth,
  gridButtonsGap = 16,
  gridCheckboxSize,
  ImageComponent,
  backgroundImageUri,
  hideBaseInput,
  toolTipContent,
  toolTipTriggerConfig,
  toolTipHoverEnabled,
  SubTextField,
  customTextColorForUnselectedCheckboxTextOutlineButtonType,
}) => {
  const [focusRef] = useAccessibilityFocus({ active, delay: 200 })
  const [focusVisible, setFocusVisible] = useState(false)
  const [hoverVisible, setHoverVisible] = useState(false)
  const focusVisibleCallback = useCallback((focus) => setFocusVisible(focus), [])
  const hoverVisibleCallback = useCallback((hover) => setHoverVisible(hover), [])
  const handleKeyPress = (event: KeyboardEvent<Element>) => {
    if (event?.key === KEYS.SPACE) {
      event.stopPropagation()
      event.preventDefault()
      onPress()
    }
  }

  const errorMessageId = customErrorMessageId ?? (error && name ? `${name}-error-message` : '')
  useSetAriaDescribedBy({ ariaDescribedBy: errorMessageId, ref: focusRef })

  const { colors, fontFamilyMetadata, isAuroraTheme } = useTheme()
  const isAuroraGridTile = isAuroraTheme && buttonType === CheckboxButtonType.GRID && !!backgroundImageUri
  const isAuroraGridTileChecked = isAuroraGridTile && checked

  const getCheckboxStylesFromState = ({
    buttonType,
    focused,
    hovered,
    pressed,
    disabled,
    selected,
    error,
  }: GetCheckboxButtonStylesArgs) => {
    type CheckboxState = 'hovered' | 'pressed' | 'inactive' | 'selected' | 'error' | 'selectedInactive'
    let state: CheckboxState | null = null
    if (hovered) state = 'hovered'
    if (pressed) state = 'pressed'
    if (disabled) state = 'inactive'
    if (selected) state = 'selected'
    if (error) state = 'error'
    if (selected && disabled) state = 'selectedInactive'

    const styleKey =
      buttonType === CheckboxButtonType.CHECKBOX_DESCRIPTION_OUTLINE
        ? CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE
        : buttonType
    const checkboxButtonStyles = getCheckboxButtonStyles(colors)[styleKey]

    const styles: ViewStyle = {
      ...checkboxButtonStyles.base,
      ...(buttonType === CheckboxButtonType.CHECKBOX_DESCRIPTION_OUTLINE && {
        marginTop: 0,
        borderColor: colors.checkboxOutlineInactive,
      }),
      ...(focused && checkboxButtonStyles.focused),
      ...(state && checkboxButtonStyles.states[state]),
      ...checkboxStyle,
    } as ViewStyle
    return styles
  }

  /** Memoize this label, otherwise it rerenders, causing it to take 2 clicks to check the field */
  const labelMarkdown = useMemo(() => {
    const bodyFontStyle = getFontStyles(colors, fontFamilyMetadata).body[labelSize]
    const mergedStyles = {
      // merge the overrides with defaults
      p2: {
        marginTop: labelSize === BodyTextSize.SMALL ? 2 : 0,
        marginBottom: 0,
        ...bodyFontStyle,
        ...(titleColor && { color: titleColor }),
        ...markdownStyle?.p2,
        ...(Platform.OS === 'android' && { lineHeight: undefined }),
      },
      // Inherit link font size from body style to match surrounding text, but keep link color
      link: {
        ...bodyFontStyle,
        color: colors.linkDefault,
        ...markdownStyle?.link,
      },
    }

    return <StyledMarkdown content={title as string} customStyles={mergedStyles} />
  }, [colors, fontFamilyMetadata, labelSize, markdownStyle?.link, markdownStyle?.p2, title, titleColor])

  if (!textContainerStyle && isTitleFocusable && IS_WEB) {
    textContainerStyle = TEXT_CONTAINER_STYLE_FOCUSABLE_TITLE
  }

  const renderTextOutline = () => {
    return (
      <TextContainer style={textContainerStyle}>
        {typeof title === 'string' ? labelMarkdown : <BodyText size={labelSize} text={title} color={titleColor} />}
      </TextContainer>
    )
  }

  const styleDescriptionAsLink = description && typeof description === 'object'

  return (
    <WithBaseInput
      label={label}
      error={error}
      name={name}
      style={baseInputStyle}
      errorMessageId={errorMessageId}
      hideErrorMessage={hideErrorMessage}
      hideBaseInput={hideBaseInput}
      toolTipContent={toolTipContent}
      toolTipTriggerConfig={toolTipTriggerConfig}
      toolTipHoverEnabled={toolTipHoverEnabled}
    >
      <CheckboxButtonContainer
        pressableRef={focusRef}
        buttonType={buttonType}
        selected={checked}
        onPress={onPress}
        style={{
          ...(buttonType === CheckboxButtonType.CHECKBOX_TEXT_OUTLINE &&
            focusVisible &&
            getFocusBoxShadow({
              innerBoxShadowSize: '6.5px',
              outerBoxShadowSize: '7.5px',
              outerBoxShadowColor: colors.primaryButtonBackgroundFocus,
              colors,
            })),
          ...(baseInputStyle?.backgroundColor && { backgroundColor: baseInputStyle.backgroundColor }),
          ...inputContainerStyle,
          ...(isAuroraGridTile && !isAuroraGridTileChecked && { overflow: 'hidden', borderWidth: 0 }),
        }}
        disabled={readOnly}
        hitSlop={20}
        testID={testID ?? tID(`${name}-Checkbox`)}
        accessibilityRole={
          !checkboxAccessibilityLabel ? AccessibilityRolesNative.CHECKBOX : AccessibilityRolesNative.TEXT
        }
        accessibilityChecked={!checkboxAccessibilityLabel ? checked : undefined}
        accessibilityLabel={accessibilityLabel ?? (typeof title === 'string' ? title : undefined)}
        accessibilityState={{ checked }}
        accessibilityLabelledBy={accessibilityLabelledBy}
        accessible={!checkboxAccessibilityLabel}
        aria-describedby={errorMessageId}
        onFocus={onFocus}
        onBlur={onBlur}
        pressedOpacity={1}
        showFocusIndicator={false}
        getFocusVisible={focusVisibleCallback}
        getHoverVisible={hoverVisibleCallback}
        hovered={hoverVisible}
        onKeyDown={handleKeyPress}
        parentContainerWidth={parentContainerWidth}
        gridButtonsGap={gridButtonsGap}
        gridCheckboxSize={gridCheckboxSize}
      >
        {isAuroraGridTile && !isAuroraGridTileChecked && (
          <ExpoImage
            source={{ uri: backgroundImageUri }}
            contentFit='cover'
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
            accessibilityIgnoresInvertColors
          />
        )}
        {(!isAuroraGridTile || isAuroraGridTileChecked) && (
          <View
            style={
              isAuroraGridTile
                ? // Offset the checkmark from the top-left corner of the grid tile to match Figma specs
                  { position: 'absolute', top: 24, left: 23 }
                : getCheckboxStylesFromState({
                    buttonType: buttonType,
                    selected: checked,
                    hovered: hoverVisible,
                    focused: focusVisible,
                    disabled: readOnly,
                    error: error,
                  })
            }
            testID={tID(`${name}-${checked}`)}
            accessibilityLabel={checkboxAccessibilityLabel}
            accessibilityRole={checkboxAccessibilityLabel ? AccessibilityRolesNative.CHECKBOX : undefined}
            accessibilityState={checkboxAccessibilityLabel ? { checked } : undefined}
            accessible={!!checkboxAccessibilityLabel}
            accessibilityChecked={checkboxAccessibilityLabel ? checked : undefined}
            importantForAccessibility={checkboxAccessibilityLabel ? 'auto' : 'no-hide-descendants'}
          >
            {checked && (
              <CheckMarkIcon
                stroke={isAuroraGridTile ? colors.checkboxTextSelected : colors.checkboxCheckSelected}
                fillColor={isAuroraGridTile ? colors.checkboxTextSelected : colors.checkboxCheckSelected}
                width='12'
                height='8'
              />
            )}
          </View>
        )}

        {children && <View style={{ flexShrink: 1 }}>{children}</View>}
        {!isTitleFocusable && (
          <>
            {buttonType === CheckboxButtonType.TEXT_OUTLINE && renderTextOutline()}
            {buttonType === CheckboxButtonType.CHECKBOX_TEXT_OUTLINE && (
              <>
                <CheckboxOutlinedTextContainer style={textContainerStyle}>
                  {!!ImageComponent && !backgroundImageUri && (
                    <CheckboxImageContainer buttonType={buttonType} size={gridCheckboxSize}>
                      <RadioButtonImageTransition scaled={hoverVisible || checked}>
                        <ImageComponent selected={checked} />
                      </RadioButtonImageTransition>
                    </CheckboxImageContainer>
                  )}
                  {Boolean(icon) && <IconContainer>{icon}</IconContainer>}
                  <OutlineText
                    size={labelSize}
                    text={title}
                    color={
                      readOnly && checked
                        ? colors.secondaryButtonTextActive
                        : readOnly
                          ? colors.primaryButtonTextActive
                          : checked
                            ? colors.secondaryButtonTextActive
                            : (customTextColorForUnselectedCheckboxTextOutlineButtonType ?? colors.inputTextActive)
                    }
                  />
                </CheckboxOutlinedTextContainer>
                {badge !== undefined && (
                  <BadgeContainer>
                    {<FieldBadge text={badge} selected={checked} variant={BadgeVariant.INFO_PRIMARY} />}
                  </BadgeContainer>
                )}
              </>
            )}
            {buttonType === CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE && (
              <CheckboxTextContainer style={textContainerStyle}>
                <BodyText
                  size={labelSize}
                  text={title}
                  color={titleColor || (error ? colors.inputTextError : colors.inputTextActive)}
                />
                {styleDescriptionAsLink ? (
                  description.visible && (
                    <Link
                      testID={tID(`${name}-description-link`)}
                      text={description.text}
                      onPress={description.onPress}
                      size={labelSize}
                      color={colors.textActive}
                      underline={description.underline}
                      medium={description.medium}
                    />
                  )
                ) : (
                  <BodyText
                    size={descriptionSize ?? labelSize}
                    text={description}
                    color={
                      descriptionColor ??
                      (styleDescriptionAsTitle
                        ? error
                          ? colors.inputTextError
                          : colors.inputTextActive
                        : colors.inputTextDefault)
                    }
                  />
                )}
              </CheckboxTextContainer>
            )}
            {buttonType === CheckboxButtonType.CHECKBOX_DESCRIPTION_OUTLINE && (
              <CheckboxTextContainer style={textContainerStyle}>
                {title && (
                  <BodyText
                    size={labelSize}
                    text={title}
                    color={error ? colors.inputTextError : colors.inputTextDefault}
                  />
                )}
                {description &&
                  (styleDescriptionAsLink ? (
                    description.visible && (
                      <Link
                        testID={tID(`${name}-description-link`)}
                        text={description.text}
                        onPress={description.onPress}
                        size={labelSize}
                        color={colors.textActive}
                        underline={description.underline}
                        medium={description.medium}
                      />
                    )
                  ) : (
                    <BodyText
                      size={labelSize}
                      text={description}
                      color={
                        styleDescriptionAsTitle
                          ? error
                            ? colors.inputTextError
                            : colors.inputTextActive
                          : colors.inputTextDefault
                      }
                    />
                  ))}
              </CheckboxTextContainer>
            )}
            {buttonType === CheckboxButtonType.GRID && (
              <>
                <GridContent style={textContainerStyle}>
                  {!!ImageComponent && !isAuroraGridTile && (
                    <CheckboxImageContainer buttonType={buttonType} size={gridCheckboxSize}>
                      <RadioButtonImageTransition scaled={hoverVisible || checked}>
                        <ImageComponent selected={checked} />
                      </RadioButtonImageTransition>
                    </CheckboxImageContainer>
                  )}
                  <GridText
                    size={labelSize}
                    text={title}
                    color={
                      readOnly && checked
                        ? colors.secondaryButtonTextActive
                        : readOnly
                          ? colors.primaryButtonTextActive
                          : checked
                            ? colors.checkboxTextSelected
                            : colors.checkboxTextDefault
                    }
                  />
                </GridContent>
                {badge !== undefined && (
                  <BadgeContainer>
                    {<FieldBadge text={badge} selected={checked} variant={BadgeVariant.INFO_PRIMARY} />}
                  </BadgeContainer>
                )}
              </>
            )}
          </>
        )}
      </CheckboxButtonContainer>
      {/* This is a workaround for 'nested focusable element' a11y violations, if title has focusable hrefs */}
      {isTitleFocusable && buttonType === CheckboxButtonType.TEXT_OUTLINE && renderTextOutline()}
      {checked && SubTextField && buttonType === CheckboxButtonType.CHECKBOX_DESCRIPTION_OUTLINE && (
        <SubTextFieldContainer>
          <SubTextField />
        </SubTextFieldContainer>
      )}
    </WithBaseInput>
  )
}

export const CheckboxRFF: FunctionComponent<FieldRenderProps<boolean>> = ({
  input: { value, onChange, name, onFocus, onBlur },
  meta: { touched, error, active, submitFailed },
  title,
  description,
  readOnly,
  labelSize,
  buttonType = CheckboxButtonType.TEXT_OUTLINE,
  badge,
  children,
  icon,
  errorOnSubmitFailed = false,
  ...rest
}) => {
  /**
   * Since we do not use a real input field with type='checkbox', checked
   * does not work with react-final-form and is never triggered. Hence we need to use
   * a type other than type='checkbox' for the field and use value here to determine if the field is true/false
   */
  const checked = Boolean(value)
  return (
    <Checkbox
      checked={checked}
      onPress={() => {
        // Checkbox is not focused on press on native mobile,
        // so if previous element that has the focus opened up the keyboard, we need to dismiss the keyboard here
        Keyboard.dismiss()
        onChange(!checked)
      }}
      error={errorOnSubmitFailed ? submitFailed && error : touched && error}
      name={name}
      title={title}
      description={description}
      readOnly={readOnly}
      labelSize={labelSize}
      onFocus={onFocus}
      onBlur={onBlur}
      buttonType={buttonType}
      badge={badge}
      active={active}
      icon={icon}
      {...rest}
    >
      {children}
    </Checkbox>
  )
}

type CheckBoxProps = {
  testID?: string
  label?: string
  title: string | React.ReactNode
  titleColor?: string
  description?:
    | string
    | { text: React.ReactNode; onPress: () => void; visible: boolean; underline?: boolean; medium?: boolean }
  checked: boolean
  onPress: () => void
  error?: string
  name?: string
  readOnly?: boolean
  labelSize?: BodyTextSize
  badge?: React.ReactNode
  buttonType?: CheckboxButtonType
  onFocus?: () => void
  onBlur?: () => void
  children?: React.ReactNode
  baseInputStyle?: ViewStyle
  markdownStyle?: { p2?: ViewStyle; link?: ViewStyle }
  active?: boolean
  accessibilityLabelledBy?: string
  accessibilityLabel?: string
  checkboxAccessibilityLabel?: string
  textContainerStyle?: ViewStyle
  styleDescriptionAsTitle?: boolean
  /** Overrides the description text size (descriptionNoOutline). Defaults to `labelSize`. */
  descriptionSize?: BodyTextSize
  /** Overrides the description text color (descriptionNoOutline). Defaults to the standard description color. */
  descriptionColor?: string
  hideErrorMessage?: boolean
  customErrorMessageId?: string
  icon?: React.ReactNode
  checkboxStyle?: ViewStyle
  inputContainerStyle?: ViewStyle
  isTitleFocusable?: boolean
  parentContainerWidth?: number
  gridButtonsGap?: number
  gridCheckboxSize?: GridCheckboxSize
  ImageComponent?: React.ComponentType<CheckImageComponentProps>
  backgroundImageUri?: string
  hideBaseInput?: boolean
  toolTipContent?: string | React.ReactElement
  toolTipTriggerConfig?: ToolTipTriggerConfig
  toolTipHoverEnabled?: boolean
  SubTextField?: React.ComponentType<any>
  customTextColorForUnselectedCheckboxTextOutlineButtonType?: string
}

type GetCheckboxButtonStylesArgs = {
  buttonType: CheckboxButtonType
  focused?: boolean
  hovered?: boolean
  pressed?: boolean
  disabled?: boolean
  selected?: boolean
  error?: string
}

export type CheckImageComponentProps = {
  width?: number
  height?: number
  selected?: boolean
}
